import { createSelector } from 'reselect';
import _intersection from 'lodash/intersection';
import { securePromoImageUrl } from 'constants';
import { Api, ContentType, Entitlements } from 'enums';
import { convertObjectToQueryString } from 'lib/format';
import bootstrapSelector from './bootstrapSelector';
import appSelector, { isDomesticSelector, editionSelector, intlSelector as _secondaryIntlSelector } from './appSelector';
import { oneIdLoggedInSelector } from 'common/selectors/oneIdSelector';
import routingSelector from './routingSelector';
import { userAuthSelector, bamSubReadySelector } from 'common/selectors/authenticationSelectors';

const { Page: { EVENT_QUERY_KEYS } } = Api;
const pageSelector = ({ page = {} }) => page;
const intlSelector = (_, { messages, intl }) => ({ messages, intl });

export default createSelector(pageSelector, (page) => page);

function meta(id, name, messages, formatMessage, contentType = ContentType.VOD, edition) {
    const opts = { name };
    const pageId = messages[`meta.title.${id}`] ? id : 'home';
    const typeQualifier = (id === 'player') ? `.${contentType}` : '';
    let title = formatMessage(messages[`meta.title.${pageId}`], opts);
    let description = formatMessage(messages[`meta.description.${pageId}${typeQualifier}`], opts);
    let keywords = formatMessage(messages['meta.keywords']);

    // Carib branding is `ESPN Play`, override the branding set in i18n file
    if (edition === 'caribbean') {
        title = title.replace(/WatchESPN/g, 'ESPN Play');
        description = description.replace(/WatchESPN/g, 'ESPN Play');
        keywords = keywords.replace(/WatchESPN/g, 'ESPN Play');
    }

    return {
        title,
        description,
        keywords
    };
}

export const metaSelector = createSelector(
    appSelector,
    routingSelector,
    pageSelector,
    editionSelector,
    intlSelector,
    _secondaryIntlSelector,
    (
        { location = {} },
        { locationBeforeTransitions = {} },
        { id = 'home', name: pageName, hero },
        { edition },
        { messages, intl: { formatMessage } = {} } = {},
        { lang }
    ) => {
        const { host, protocol, path, baseUrl, query: origQuery } = location;
        const { basename = baseUrl, pathname = path, query = origQuery } = locationBeforeTransitions || {};

        // declare properties that can change depending on the path or if an event query is passed
        let canonicalUrl = `${protocol}://${host}${basename}${pathname}`;
        const sanatizedCanonicalUrl = (canonicalUrl.slice(-1) === '/') ? canonicalUrl.slice(0, -1) : canonicalUrl;
        const langQueryParam = (lang && lang !== '') ? `lang=${lang}` : '';
        let name = pageName;
        let contentType;
        let socialUrl = sanatizedCanonicalUrl;
        let socialDescription;
        let socialTitle;
        let socialImageUrl = securePromoImageUrl;
        let noIndex = false;
        let canonicalQuery = '';
        // Only use the content's meta information if linked specifically to an event
        const queryKeys = Object.keys(query);
        const matchedEventQueryKeys = _intersection(queryKeys, EVENT_QUERY_KEYS);

        if (matchedEventQueryKeys.length > 0 && hero) {
            const { description, imageHref, name: heroName, status, type } = hero;
            contentType = type;

            // only use event query in the meta urls
            const matchedEventQueryValue = convertObjectToQueryString(query, matchedEventQueryKeys);
            canonicalQuery = (matchedEventQueryValue && matchedEventQueryValue !== '') && `?${matchedEventQueryValue}`;

            // Handle adding the lang query param properly when other params are in place
            if (canonicalQuery !== '' && langQueryParam !== '') {
                canonicalQuery = `${canonicalQuery}&${langQueryParam}`;
            } else if (canonicalQuery === '' && langQueryParam !== '') {
                canonicalQuery = `?${langQueryParam}`;
            }

            // social meta tags should always reflect the content specified in the url
            socialTitle = heroName;
            socialImageUrl = imageHref;
            socialDescription = description;
            socialUrl = `${sanatizedCanonicalUrl}${canonicalQuery}`;

            // canonical url for the player page should include the event query
            if (pathname === '/player') {
                name = heroName;
                canonicalUrl = `${sanatizedCanonicalUrl}${canonicalQuery}`;
            }

            // TODO: serve `robots.txt` from Varnish for `www.espnsb.com`
            if (status === 'over' || host.indexOf('espnsb.com') > -1) {
                noIndex = true;
            }
        }

        const metaProps = meta(id, name, messages, formatMessage, contentType, edition);

        return {
            ...metaProps,
            canonicalUrl,
            socialImageUrl,
            socialDescription: socialDescription || metaProps.description,
            socialTitle: socialTitle || metaProps.title,
            socialUrl,
            noIndex
        };
    }
);

export const pageEditionSelector = createSelector(
    pageSelector,
    ({ edition = {} }) => edition
);

export const brandingSelector = createSelector(
    bootstrapSelector,
    pageSelector,
    editionSelector,
    pageEditionSelector,
    ({ branding: { editions = {} } = {} }, { id }, { edition }, { branding = {} }) => {
        if (editions[edition]) { // override from bootstrap
            const { name, logoUrl, lightLogoUrl } = editions[edition];
            branding.name = name;
            branding.logoUrl = logoUrl;
            branding.lightLogoUrl = lightLogoUrl;
        }

        // black background pages
        if (id === 'player' || id === 'syndicated-player') {
            branding.logoUrl = branding.lightLogoUrl;
        }

        return branding;
    }
);

export const isFinalSelector = createSelector(
    appSelector,
    isDomesticSelector,
    ({ packageId }, isDomestic) => (
        isDomestic ||
        typeof packageId !== 'undefined'
    )
);

export const competitorSelector = createSelector(
    pageSelector,
    (page) => (page.competitors)
);

// Caution: do not use this alone, combine with page name check
export const showPromoSelector = createSelector(
    isDomesticSelector,
    oneIdLoggedInSelector,
    userAuthSelector,
    bamSubReadySelector,
    (isDomestic, isLoggedIn, entitlements, bamSubReady) => {
        if (!process.browser) {
            return false;
        }
        return (
            isDomestic &&
            (!isLoggedIn || (isLoggedIn && bamSubReady && entitlements &&
                (entitlements.indexOf(Entitlements.ESPN_PLUS) === -1 &&
                entitlements.indexOf(Entitlements.ESPN_TDA) === -1)
            ))
        );
    }
);

// FIXME: couldn't define this in `appSelector` because of the `reselect` issue
// https://github.com/reactjs/reselect/issues/169
export const appReadySelector = createSelector(
    appSelector,
    // pageSelector,
    // ({ rendered }, { id, scored }) => (rendered && (id === 'schedule' || scored))
    ({ rendered }) => rendered
);

export const pagePathWithPageId = createSelector(
    pageSelector,
    appSelector,
    (
        { id = 'index' },
        { location: { baseUrl = '/watch' } = {} }
    ) => {
        const pageId = (id === 'home') ? 'index' : id;

        return `${baseUrl}/${pageId}`;
    }
);



// WEBPACK FOOTER //
// ./app/common/selectors/pageSelector.js